import type { PredictorConfig } from "../engine/config";
import { formatGrind, grindTicks } from "../app/grindFormat";
import type { Grinder, Prediction, Shot, Taste } from "../engine/model";

/**
 * Dial-in chart: ln(normalised time) against grind, with the fitted line ln t = lnIntercept + slope·x. The Y axis is
 * logarithmic but labelled in seconds. A faint band marks the recipe time window around the effective target,
 * a brass line marks the recommendation, and each shot is a taste-coded point that differs by shape as well as
 * colour: ● balanced, ▲ sour, ■ bitter, ○ no taste. Colours come from CSS tokens so they hold up in dark mode.
 */
const TASTE_VAR: Record<Taste | "none", string> = {
  balanced: "var(--taste-balanced)", sour: "var(--taste-sour)", bitter: "var(--taste-bitter)", none: "var(--taste-none)",
};

export function Marker(props: { taste: Taste | null; cx: number; cy: number; r: number }) {
  const { cx, cy, r } = props;
  const color = TASTE_VAR[props.taste ?? "none"];
  switch (props.taste) {
    case "balanced":
      return <circle cx={cx} cy={cy} r={r} style={{ fill: color, stroke: "var(--card)" }} stroke-width={1.5} />;
    case "sour":
      return <polygon points={`${cx},${cy - r * 1.15} ${cx + r * 1.1},${cy + r * 0.8} ${cx - r * 1.1},${cy + r * 0.8}`}
                      style={{ fill: color, stroke: "var(--card)" }} stroke-width={1.5} />;
    case "bitter":
      return <rect x={cx - r * 0.9} y={cy - r * 0.9} width={r * 1.8} height={r * 1.8} style={{ fill: color, stroke: "var(--card)" }} stroke-width={1.5} />;
    default:
      return <circle cx={cx} cy={cy} r={r - 0.5} style={{ fill: "var(--card)", stroke: color }} stroke-width={2} />;
  }
}

export function Legend() {
  const item = (taste: Taste | null, label: string) => (
    <span>
      <svg viewBox="0 0 12 12" aria-hidden="true"><Marker taste={taste} cx={6} cy={6} r={4.2} /></svg>
      {label}
    </span>
  );
  return (
    <div class="legend" aria-label="Legend">
      {item("balanced", "Balanced")}{item("sour", "Sour")}{item("bitter", "Bitter")}{item(null, "No taste")}
      <span><svg viewBox="0 0 12 12" aria-hidden="true"><rect x="0" y="3" width="12" height="6" style={{ fill: "var(--band)" }} /></svg>Target</span>
    </div>
  );
}

const SECOND_TICKS = [5, 10, 15, 20, 25, 30, 35, 40, 50, 60, 80, 100];

export function TrendChart(props: { shotsNewestFirst: Shot[]; prediction: Prediction; grinder: Grinder; cfg: PredictorConfig }) {
  const { prediction: p, grinder: g, cfg } = props;
  const W = 340, H = 190, PL = 34, PR = 12, PT = 10, PB = 28;
  const shots = [...props.shotsNewestFirst].slice(0, 10);
  if (shots.length === 0) return null;

  const tNorm = (s: Shot) => (s.timeSeconds * cfg.targetYieldG) / s.yieldG;
  const lo = cfg.targetTimeS - cfg.targetWindowHalfS, hi = cfg.targetTimeS + cfg.targetWindowHalfS;

  let minX = Math.min(p.grindSetting, ...shots.map((s) => s.grindSetting));
  let maxX = Math.max(p.grindSetting, ...shots.map((s) => s.grindSetting));
  const span = Math.max(maxX - minX, 6 * g.dialResolution);
  const mid = (minX + maxX) / 2;
  minX = Math.max(g.minGrind, mid - span / 2 - g.dialResolution);
  maxX = Math.min(g.maxGrind, mid + span / 2 + g.dialResolution);
  if (maxX - minX < 2 * g.dialResolution) { minX = g.minGrind; maxX = g.maxGrind; }

  const hasLine = !Number.isNaN(p.lnIntercept);
  const lnLine = (x: number) => p.lnIntercept + p.slopeUsed * x;
  const lns = [Math.log(lo), Math.log(hi), ...shots.map((s) => Math.log(tNorm(s)))];
  if (hasLine) lns.push(lnLine(minX), lnLine(maxX));
  let minY = Math.min(...lns) - 0.06, maxY = Math.max(...lns) + 0.06;
  minY = Math.max(Math.log(4), minY);
  maxY = Math.min(Math.log(120), maxY);
  if (maxY - minY < 0.3) { const c = (maxY + minY) / 2; minY = c - 0.15; maxY = c + 0.15; }

  const sx = (x: number) => PL + ((x - minX) / (maxX - minX)) * (W - PL - PR);
  const sy = (ln: number) => PT + ((maxY - ln) / (maxY - minY)) * (H - PT - PB);
  const clampY = (ln: number) => Math.min(maxY, Math.max(minY, ln));

  let yTicks = SECOND_TICKS.filter((t) => Math.log(t) >= minY && Math.log(t) <= maxY);
  if (yTicks.length > 5) yTicks = yTicks.filter((_, i) => i % 2 === 0);
  const xTicks = grindTicks(minX, maxX, g);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Dial-in history: shot time against grind, with fitted line and target window">
      <rect x={PL} y={sy(Math.log(hi))} width={W - PL - PR} height={Math.max(0, sy(Math.log(lo)) - sy(Math.log(hi)))} style={{ fill: "var(--band)" }} />
      {yTicks.map((t) => (
        <g key={`y${t}`}>
          <line x1={PL} y1={sy(Math.log(t))} x2={W - PR} y2={sy(Math.log(t))} style={{ stroke: "var(--grid)" }} />
          <text x={PL - 6} y={sy(Math.log(t)) + 4} text-anchor="end" font-size="10" style={{ fill: "var(--muted)" }}>{t}s</text>
        </g>
      ))}
      {xTicks.map((x, i) => (
        <g key={`x${i}`}>
          <line x1={sx(x)} y1={PT} x2={sx(x)} y2={H - PB} style={{ stroke: "var(--grid)" }} />
          <text x={sx(x)} y={H - PB + 14} text-anchor="middle" font-size="10" style={{ fill: "var(--muted)" }}>{formatGrind(x, g)}</text>
        </g>
      ))}
      {hasLine && (
        <line x1={sx(minX)} y1={sy(clampY(lnLine(minX)))} x2={sx(maxX)} y2={sy(clampY(lnLine(maxX)))}
              style={{ stroke: "var(--fit)" }} stroke-width={2} />
      )}
      <line x1={sx(p.grindSetting)} y1={PT} x2={sx(p.grindSetting)} y2={H - PB} style={{ stroke: "var(--brass)" }} stroke-width={1.5} />
      {shots.map((s) => (
        <Marker key={s.id ?? s.timestamp} taste={s.taste} cx={sx(s.grindSetting)} cy={sy(clampY(Math.log(tNorm(s))))} r={5} />
      ))}
    </svg>
  );
}
